import React, { memo } from "react";

import CompositeMetric from "@/components/molecules/CompositeMetric";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";

import { isClickableMetric } from "@/constants/metrics";
import { ICompositeMetric } from "@/stores/compositeMetric";
import { ifWeb } from "@/utils/platform";

/**
 * Type for factory function that creates memoized handlers
 */
type CreateMetricPressHandler = (metric: ICompositeMetric) => () => void;

/**
 * Props for MetricsGridView component
 */
interface Props {
  metrics?: ICompositeMetric[][] | null;
  isSmallScreen: boolean;
  isDark: boolean;
  createMetricPressHandler: CreateMetricPressHandler;
}

/**
 * View component for rendering a grid of composite metrics
 * Displays metrics in rows with clickable metric cards
 * @param root0 - Component props
 * @param root0.metrics - 2D array of composite metrics to display
 * @param root0.isSmallScreen - Whether the screen is mobile-sized (affects styling)
 * @param root0.isDark - Whether dark theme is active
 * @param root0.createMetricPressHandler - Factory function from Container to create memoized handlers per metric
 */
const MetricsGridView = ({
  metrics,
  isSmallScreen,
  isDark,
  createMetricPressHandler,
}: Props) => (
  <Stack className="flex-1 gap-4 pt-4">
    {metrics?.map((playerCompositeMetrics, i) => (
      <Stack
        direction="horizontal"
        key={`player_detail_${i.toString()}`}
        className="items-start gap-4"
      >
        {playerCompositeMetrics.map(
          compositeMetric =>
            compositeMetric && (
              <Box className="flex-1" key={compositeMetric.label}>
                <Pressable
                  className="flex-1"
                  // eslint-disable-next-line no-restricted-syntax -- data-driven web-only cursor (pointer/auto via isClickableMetric); no closed cursor token exists
                  UNSAFE_style={ifWeb(
                    {
                      cursor: isClickableMetric(compositeMetric.label)
                        ? ("pointer" as const)
                        : ("auto" as const),
                    },
                    {}
                  )}
                  onPress={createMetricPressHandler(compositeMetric)}
                >
                  <CompositeMetric
                    label={compositeMetric.label}
                    formattedValue={compositeMetric.formattedValue}
                    styles={compositeMetric.styles}
                    isSmallScreen={isSmallScreen}
                    isDark={isDark}
                  />
                </Pressable>
              </Box>
            )
        )}
      </Stack>
    ))}
  </Stack>
);
MetricsGridView.displayName = "MetricsGridView";
export default memo(MetricsGridView);
